"use client";

import { Brain, Target, TrendingUp, Users, CheckCircle } from "lucide-react";
import { useScrollAnimation } from "../hooks/useScrollAnimation";
import { useState } from "react";

export default function Research() {
  const [activeSection, setActiveSection] = useState("approach");
  const heroAnimation = useScrollAnimation({ delay: 0 });
  const sectionsAnimation = useScrollAnimation({ delay: 200 });

  const sections = {
    approach: {
      title: "Our Approach",
      content: (
        <div className="space-y-6">
          <p className="text-lg text-[#2b2b2b] leading-relaxed">
            At AnamVR, science isn't an afterthought — it's the foundation.
            Everything we create is informed by established therapeutic
            approaches and developed in close collaboration with mental health
            professionals and researchers.
          </p>
          <div className="flex flex-col">
            {[
              "Cognitive Behavioural Therapy (CBT)",
              "Mindfulness-Based Cognitive Therapy (MBCT)",
              "Trauma-Informed Design",
              "Behavioural Activation and Exposure Techniques",
              "Psychoeducation through Gamified Interventions",
            ].map((item, index) => (
              <div
                key={index}
                className="flex items-center gap-3 p-1 rounded-lg"
              >
                <CheckCircle className="w-4 h-4 text-purple-600 flex-shrink-0" />
                <span className="text-lg text-[#2b2b2b]">{item}</span>
              </div>
            ))}
          </div>
          <p className="text-gray-600">
            This combination enables us to deliver experiences that are both
            emotionally engaging and clinically meaningful — designed not just
            to feel good, but to do good.
          </p>
        </div>
      ),
    },
    benefits: {
      title: "The Benefits",
      content: (
        <div className="space-y-8">
          <p className="text-lg text-[#2b2b2b] leading-relaxed">
            Research demonstrates that VR-based mental health interventions can
            significantly improve wellbeing outcomes. Our early data shows
            consistent positive results for users who engage regularly with
            AnamVR experiences.
          </p>

          {/* Key Statistics Grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              {
                stat: "70%",
                label: "Mood Improvement",
                description: "noticeable within 2-4 weeks",
                color: "purple",
              },
              {
                stat: "45%",
                label: "Drop in Stress",
                description:
                  "among users who use AnamVR four times a week for a month",
                color: "blue",
              },
              {
                stat: "4x",
                label: "Retention Rate",
                description:
                  "21.5% average retention compared to 3.9% industry standard",
                color: "green",
              },
            ].map((item, index) => (
              <div
                key={index}
                className="bg-white border border-gray-200 rounded-xl p-5 text-center"
              >
                <div
                  className={`text-4xl font-bold mb-2 ${
                    item.color === "purple"
                      ? "text-purple-600"
                      : item.color === "blue"
                        ? "text-blue-600"
                        : "text-green-600"
                  }`}
                >
                  {item.stat}
                </div>
                <h4 className="text-lg font-semibold text-[#2b2b2b] mb-2">
                  {item.label}
                </h4>
                <p className="text-sm text-gray-600 leading-relaxed">
                  {item.description}
                </p>
              </div>
            ))}
          </div>
          <p className="text-gray-600 text-sm">
            *These findings are based on in-app usage data, self-reported mood
            tracking, and follow-up surveys — all anonymised and ethically
            managed.
          </p>
        </div>
      ),
    },
    research: {
      title: "Our Research",
      content: (
        <div className="space-y-6">
          <p className="text-lg text-[#2b2b2b] leading-relaxed">
            We are actively working with academic partners to carry out
            comprehensive research studies.
          </p>
          <div className="flex flex-col">
            {[
              "<strong>Randomised Controlled Trials (RCTs)</strong> to validate outcomes",
              "<strong>Longitudinal studies</strong> tracking long-term changes in mental health metrics",
              "<strong>Comparative studies</strong> benchmarking against traditional digital wellbeing tools",
              "<strong>Qualitative research</strong> on user experience, inclusion, and cultural relevance",
            ].map((item, index) => (
              <div
                key={index}
                className="flex items-center gap-3 p-1 rounded-lg"
              >
                <CheckCircle className="w-4 h-4 text-purple-600 flex-shrink-0" />
                <span
                  className="text-lg text-[#2b2b2b]"
                  dangerouslySetInnerHTML={{ __html: item }}
                />
              </div>
            ))}
          </div>
          <p className="text-gray-600">
            We’re committed to making our findings publicly available and
            contributing to the wider field of digital mental health.
          </p>
        </div>
      ),
    },
  };

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Hero Section */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 pt-20 sm:pt-24 md:pt-32 pb-12 sm:pb-16">
        <div
          ref={heroAnimation.ref}
          className={`flex flex-col-reverse mt-20 sm:mt-6 lg:grid lg:grid-cols-3 gap-8 lg:gap-12 items-center transition-all duration-700 ${
            heroAnimation.isVisible ? "animate-slide-up" : "opacity-0"
          }`}
        >
          {/* Text Content */}
          <div className="flex flex-col lg:col-span-2 justify-center w-full order-2 lg:order-1">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-[#2b2b2b] mb-4 sm:mb-6 leading-tight">
              The Science <br className="hidden sm:block" />
              Behind{" "}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-500 to-purple-800">
                AnamVR
              </span>
            </h1>
            <p className="text-lg sm:text-xl text-gray-600 leading-relaxed max-w-2xl">
              Our goal is to improve mental health and wellbeing through
              immersive technology — but the only way to know if we're truly
              accomplishing that is to measure, test, and study it. That's where
              our research comes in.
            </p>
          </div>

          {/* Chart Visualization */}
          <div className="flex justify-center lg:justify-end w-full order-1 lg:order-2">
            <div className="relative">
              {/* 3D Chart Visualization */}
              <div className="flex items-end gap-3 sm:gap-4 h-48 sm:h-56 md:h-64">
                <div className="w-12 sm:w-14 md:w-16 h-24 sm:h-28 md:h-32 bg-gradient-to-t from-blue-400 to-blue-500 rounded-t-lg shadow-lg"></div>
                <div className="w-12 sm:w-14 md:w-16 h-20 sm:h-20 md:h-24 bg-gradient-to-t from-blue-500 to-blue-600 rounded-t-lg shadow-lg"></div>
                <div className="w-12 sm:w-14 md:w-16 h-36 sm:h-42 md:h-48 bg-gradient-to-t from-purple-400 to-purple-500 rounded-t-lg shadow-lg"></div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div
        ref={sectionsAnimation.ref}
        className={`max-w-6xl mx-auto px-6 py-10 transition-all duration-700 ${
          sectionsAnimation.isVisible ? "animate-slide-up" : "opacity-0"
        }`}
      >
        <div className="border-b border-gray-200 mb-8">
          <div className="flex flex-nowrap gap-6 overflow-x-auto scrollbar-none">
            {Object.entries(sections).map(([key, section]) => (
              <button
                key={key}
                onClick={() => setActiveSection(key)}
                className={`flex items-center gap-2 pb-4 border-b-2 transition-colors ${
                  activeSection === key
                    ? "border-purple-600 text-purple-600"
                    : "border-transparent text-gray-500 hover:text-gray-700"
                }`}
              >
                <span className="font-medium">{section.title}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Active Section Content */}
        <div className="pb-16">
          <div className="max-w-4xl">
            <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center gap-3">
              {sections[activeSection as keyof typeof sections].title}
            </h2>
            {sections[activeSection as keyof typeof sections].content}
          </div>
        </div>
      </div>
    </div>
  );
}
